import axios from 'axios'
import * as Sentry from '@sentry/react'
import { refreshAccessToken } from './src/utils/auth.js'

const DEFAULT_TIMEOUT_MS = 50000

// Generate a v4 UUID without a polyfill cost — crypto.randomUUID is supported
// in every browser we ship to (Chrome 92+, Safari 15.4+, Firefox 95+).
function genRequestId() {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID()
  }
  // Fallback for very old test environments — RFC4122 v4 from Math.random.
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0
    const v = c === 'x' ? r : (r & 0x3) | 0x8
    return v.toString(16)
  })
}

const instance = axios.create({
  baseURL: import.meta.env.VITE_API_BASE_URL,
  timeout: Number(import.meta.env.VITE_API_TIMEOUT_MS || DEFAULT_TIMEOUT_MS)
})

instance.interceptors.request.use(async (config) => {
  const expiry = parseInt(localStorage.getItem('token_expiry') || '0', 10);
  const now = Date.now();

  // Only attempt a token refresh when an expiry time exists
  if (expiry && expiry - now < 60 * 1000) {
    const newToken = await refreshAccessToken();
    if (!newToken) {
      // When refresh fails, fallback to login for authenticated views
      window.location.href = '/login';
      return config;
    }
  }

  const token = localStorage.getItem('access_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }

  // Phase 8 — generate a client-side request id on every outbound request so
  // backend logs, FE Sentry breadcrumbs, and audit events can all be joined
  // even when the request never reaches the server (network failure, abort).
  // Backend `RequestContextMiddleware` accepts inbound `x-request-id` and
  // echoes it back unchanged.
  const requestId = genRequestId()
  config.headers['x-request-id'] = requestId
  config.metadata = { requestId, startTs: Date.now() }

  return config;
});

instance.interceptors.response.use(
  res => {
    const requestId = res.headers?.['x-request-id'] || res.config?.metadata?.requestId;
    if (requestId) {
      localStorage.setItem('last_request_id', requestId);
      // Tag Sentry so any error captured shortly after auto-includes the id.
      try { Sentry.setTag('last_request_id', requestId) } catch { /* Sentry not init */ }
    }
    // Phase 8 — record a structured breadcrumb on every successful request so
    // exceptions later in the session have a request trail. Body is never
    // captured (PII risk); redact.ts also strips bodies as a backstop.
    try {
      const start = res.config?.metadata?.startTs
      Sentry.addBreadcrumb({
        category: 'http',
        type: 'http',
        level: 'info',
        data: {
          method: (res.config?.method || 'get').toUpperCase(),
          url: res.config?.url,
          status_code: res.status,
          request_id: requestId,
          duration_ms: start ? Date.now() - start : undefined,
        },
      })
    } catch { /* Sentry not init */ }
    return res;
  },
  async err => {
    const cfg = err?.config
    const requestId = err?.response?.headers?.['x-request-id'] || cfg?.metadata?.requestId
    const status = err?.response?.status

    // Backstop: if the proactive refresh in the request interceptor missed
    // (e.g., token_expiry was stale, or WorkOS revoked the access-token
    // early), the request lands as 401 with TOKEN_EXPIRED. Refresh once and
    // retry — but only once per original request to avoid infinite loops.
    if (status === 401 && cfg && !cfg._retriedAfter401 && cfg.url && !cfg.url.includes('/auth/')) {
      cfg._retriedAfter401 = true
      try {
        const newToken = await refreshAccessToken()
        if (newToken) {
          cfg.headers = cfg.headers || {}
          cfg.headers.Authorization = `Bearer ${newToken}`
          return instance.request(cfg)
        }
      } catch { /* fall through to original error */ }
    }

    try {
      const start = cfg?.metadata?.startTs
      Sentry.addBreadcrumb({
        category: 'http',
        type: 'http',
        level: 'error',
        data: {
          method: (cfg?.method || 'get').toUpperCase(),
          url: cfg?.url,
          status_code: err?.response?.status,
          request_id: requestId,
          duration_ms: start ? Date.now() - start : undefined,
          error_message: err?.message,
        },
      })
    } catch { /* Sentry not init */ }

    // Phase 9 — global 429 rate-limit signal. Components that import the axios
    // instance get the rejected promise as usual; the toast layer also listens
    // for this event and surfaces a user-visible cooldown message.
    if (err?.response?.status === 429) {
      try {
        const retryAfter = parseInt(err.response.headers?.['retry-after'] || '0', 10)
        window.dispatchEvent(new CustomEvent('axios:rate-limited', {
          detail: {
            retryAfterSeconds: Number.isFinite(retryAfter) && retryAfter > 0 ? retryAfter : 60,
            url: cfg?.url,
            requestId,
          },
        }))
      } catch { /* CustomEvent unavailable in SSR/test */ }
    }

    // Phase 9 — global AI-service-unavailable signal. AI endpoints surface as
    // 502/503/504 when upstream LLM is down or timing out; the UI gets a
    // dedicated toast instead of a generic axios error.
    const url = cfg?.url || ''
    const isAiPath = /\/(marketing[-_]kits?|ai|generate|outreach|comps?|valuation|leads?)\b/.test(url)
    if (isAiPath && (status === 502 || status === 503 || status === 504)) {
      try {
        window.dispatchEvent(new CustomEvent('axios:ai-unavailable', {
          detail: { status, url, requestId },
        }))
      } catch { /* CustomEvent unavailable */ }
    }

    return Promise.reject(err);
  }
)

export default instance;
